import { pdf } from "@react-pdf/renderer";
import { toPng } from "html-to-image";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import MonitorService from "../../../api/MonitorService";
import PropertyAreaService from "../../../api/PropertyAreaService";
import PropertyService from "../../../api/PropertyService";
import { CustomButton } from "../../../components/CustomButton/CustomButton";
import { Filters } from "../../../components/Filters/Filters";
import { LoadingSpinner } from "../../../components/LoadingSpinner/LoadingSpinner";
import { NoData } from "../../../components/NoData/NoData";
import { MonitorPlanPDF } from "../../../components/PDFDocument/MonitorPlanPDF";
import PinCropMap from "../../../components/PinCropMap/PinCropMap";
import { SubMenuButton } from "../../../components/SubMenuButton/SubMenuButton";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import { MonitorsIcon } from "../../../icons/MonitorsIcon/MonitorIcon";
import { AppLayout } from "../../../layout/AppLayout/AppLayout";
import { RootState } from "../../../store";
import { userSliceActions } from "../../../store/user-slice";
import styles from "../../../styles/customer-monitors.module.css";
import { Document } from "../../../types/document";
import { Crop } from "../../../types/map";
import { MonitorPinAbbr } from "../../../types/map";
import { MonitorReport } from "../../../types/monitor";
import { Meta } from "../../../types/response";

import { MonitorDetails } from "./MonitorDetails";

interface PropertyArea {
  id: string;
  name: string;
  map: Document | null;
  monitors: any[];
}

export type MonitorDetailsType = {
  id: string;
  name: string;
  number: string;
  location: string;
  isTemporary: boolean;
  isWireless: boolean;
  wirelessId: string | null;
  reports: MonitorReport[];
};

export type Product = {
  name: string;
  quantity: string;
};

export type MonitorTypeItem = {
  id: string;
  label: string;
};

export const Monitors = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const mapRef = useRef<HTMLDivElement>(null);

  const pendingRedirectUrl = useSelector(
    (state: RootState) => state.user.pendingRedirectUrl
  );

  // Get deep link params from URL or from pendingRedirectUrl if it exists
  let deepLinkPropertyAreaId = searchParams.get("property_area_id");
  let deepLinkMonitorId = searchParams.get("monitor_id");
  let deepLinkReportId = searchParams.get("report_id");
  let deepLinkAction = searchParams.get("action");

  // If no search params but pendingRedirectUrl exists, parse it
  if (!deepLinkPropertyAreaId && pendingRedirectUrl) {
    const url = new URL(pendingRedirectUrl, window.location.origin);
    deepLinkPropertyAreaId = url.searchParams.get("property_area_id");
    deepLinkMonitorId = url.searchParams.get("monitor_id");
    deepLinkReportId = url.searchParams.get("report_id");
    deepLinkAction = url.searchParams.get("action");
  }

  const [propertyAreas, setPropertyAreas] = useState<any[]>([]);
  const [subMenuButtons, setSubMenuButtons] = useState<string[]>([]);
  const [monitors, setMonitors] = useState<any[]>([]);
  const [map, setMap] = useState<Document | null>(null);
  const [crops, setCrops] = useState<Crop[]>([]);
  const [monitorPinAbbrs, setMonitorPinAbbrs] = useState<MonitorPinAbbr[]>([]);
  const [monitorTypes, setMonitorTypes] = useState<MonitorTypeItem[]>([]);
  const [currentTab, setCurrentTab] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [message, setMessage] = useState("");
  const selectedProperty = useSelector(
    (state: RootState) => state.user?.selectedProperty?.id
  );
  const properties = useSelector((state: RootState) => state.user?.properties);

  const [monitorDetails, setMonitorDetails] = useState<MonitorDetailsType>({
    id: "",
    name: "",
    number: "",
    location: "",
    isTemporary: false,
    isWireless: false,
    wirelessId: null,
    reports: [],
  });
  const prevMonitorId = useRef<string | null>(null);

  const [selectedArea, setSelectedArea] = useState(0);
  const [openMonitorDetails, setOpenMonitorDetails] = useState(true);
  const [isPinSelected, setIsPinSelected] = useState(false);
  const [lastServicesData, setLastServicesData] = useState<any>([]);
  const [lastServicesIncludes, setLastSetvicesIncludes] = useState<any>([]);
  const [documents, setDocuments] = useState<any>([]);
  const [monitorProducts, setMonitorProducts] = useState<any>([]);
  const [resultMeta, setResultMeta] = useState<Meta>();
  const scrollableWrapper = document.getElementById(
    "monitor-details"
  ) as HTMLElement;
  const [hasMore, setHasMore] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [isLoadingLastServies, setIsLoadingLastServices] = useState(false);
  const [selectedMonitorTypes, setSelectedMonitorTypes] = useState<string[]>(
    []
  );
  const [selectedStationar, setSelectedStationar] = useState<string[]>([]);
  const [selectedTrapTypes, setSelectedTrapTypes] = useState<string[]>([]);
  const [selectedCheckbox, setSelectedCheckbox] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [filtered, setFiltered] = useState(false);
  const [pendingDeepLink, setPendingDeepLink] = useState<{
    propertyAreaId: string;
    monitorId: string;
    reportId: string;
  } | null>(null);
  const [selectedPinIndex, setSelectedPinIndex] = useState<number | null>(null);

  const [isFetching, setIsFetching] = useInfiniteScroll(
    scrollableWrapper,
    hasMore
  );

  const stationarTypes = [
    { id: 1, name: "temporär", value: "isTemporary" },
    { id: 2, name: "digital", value: "isWireless" },
  ];

  const trapTypeOptions = [
    { id: 3, name: "Fangfalle", value: "animalFound" },
    { id: 4, name: "Köderfalle", value: "baitEaten" },
  ];

  const statusesOptions = [
    { id: 1, name: "Online", value: "online" },
    { id: 2, name: "Offline", value: "offline" },
    { id: 3, name: "Fehlauslösung", value: "false_triggering" },
    { id: 4, name: "Ausgelöst", value: "trap_triggered" },
  ];

  // If deep link property area ID exists, fetch the property and select it
  useEffect(() => {
    if (deepLinkPropertyAreaId) {
      PropertyService.GetPropertyArea(deepLinkPropertyAreaId)
        .then((response) => {
          const propertyId = response.data.propertyId;
          const property = properties?.find((p) => p.id === propertyId);
          if (property) {
            dispatch(userSliceActions.setSelectedPropertyId(property.id));
            dispatch(userSliceActions.setSelectedProperty(property));
          }
        })
        .catch((error) =>
          console.error("Error fetching property area:", error)
        );
    }
  }, [deepLinkPropertyAreaId, properties, dispatch]);

  // Handle pending redirect - navigate to the URL if it exists
  useEffect(() => {
    if (pendingRedirectUrl && propertyAreas && propertyAreas.length > 0) {
      const currentUrl = window.location.pathname + window.location.search;
      // Only redirect if we're not already on the pending URL
      if (currentUrl !== pendingRedirectUrl) {
        navigate(pendingRedirectUrl);
      }
      dispatch(userSliceActions.setPendingRedirectUrl(null));
    }
  }, [pendingRedirectUrl?.length]);

  // Set pending deep link from the extracted query params
  useEffect(() => {
    if (
      deepLinkPropertyAreaId &&
      deepLinkMonitorId &&
      deepLinkReportId &&
      deepLinkAction === "add_comment"
    ) {
      setPendingDeepLink({
        propertyAreaId: deepLinkPropertyAreaId,
        monitorId: deepLinkMonitorId,
        reportId: deepLinkReportId,
      });
    }
  }, [
    deepLinkPropertyAreaId,
    deepLinkMonitorId,
    deepLinkReportId,
    deepLinkAction,
  ]);

  useEffect(() => {
    setDocuments([]);
    setLastServicesData([]);
    setLastSetvicesIncludes([]);
    setPage(1);
  }, [isPinSelected, selectedArea, monitorDetails, selectedProperty]);

  useEffect(() => {
    if (isFetching) {
      setPage((prevPage) => prevPage + 1);
      setIsFetching(false);
    }
  }, [isFetching]);

  useEffect(() => {
    if (resultMeta) {
      setHasMore(resultMeta?.currentPage < resultMeta?.totalPages);
    }
  }, [resultMeta]);

  useEffect(() => {
    const idNotChanged = prevMonitorId.current == monitorDetails.id;
    if (idNotChanged) {
      FetchMonitorLastServices(monitorDetails.id);
    }
  }, [page]);

  useEffect(() => {
    getProperties();
  }, [selectedProperty]);

  const getProperties = async () => {
    setLoading(true);
    setLoadingDetails(true);
    setMessage("");
    await PropertyService.GetProperty(selectedProperty)
      .then((property) => {
        const areas =
          property?.data?.propertyAreas
            ?.filter((area) => !area.isHidden)
            .map((area) => ({
              id: area.id,
              name: area.label,
              map: area.map,
              monitors: area.includes.monitors,
            })) ?? [];

        setPropertyAreas(areas);
        if (areas.length === 0) {
          setSubMenuButtons([]);
          setMessage("Für dieses Objekt gibt es keine Bereiche");
        } else {
          const dynamicButtons = areas.map((area) => area.name);
          setSubMenuButtons(dynamicButtons);
          setCurrentTab(areas[0].name);
          changeMap(0, areas);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
        setLoadingDetails(false);
      });
  };

  useEffect(() => {
    if (!pendingDeepLink || propertyAreas.length === 0) return;

    const areaIndex = propertyAreas.findIndex(
      (a) => a.id === pendingDeepLink.propertyAreaId
    );

    if (areaIndex !== -1) {
      handleChangeTab(propertyAreas[areaIndex].name, areaIndex);
    }
  }, [propertyAreas, pendingDeepLink]);

  const handlePinClick = async (index: number, monitorsOverride?: any[]) => {
    const monitorList = monitorsOverride || monitors; // use passed-in monitors or fallback to state
    const monitor = monitorList[index];

    setSelectedPinIndex(index); //highlight selected pin (when coming here from email link)
    setIsPinSelected(true);

    const isIdChanged = prevMonitorId.current != monitor.id;
    if (isIdChanged) {
      prevMonitorId.current = monitor.id;
      setMonitorDetails({
        id: monitor.id,
        name: monitor.monitorType.label,
        number: monitor.position,
        location: monitor.location,
        isTemporary: monitor.isTemporary,
        isWireless: monitor.isWireless,
        wirelessId: monitor.wirelessId,
        reports: monitor.traplinkedReports || [],
      });
      try {
        setLoadingDetails(true);
        const resultProducts: any =
          await MonitorService.GetMonitorCurrentProducts(monitor.id);

        if (resultProducts && Array.isArray(resultProducts.data)) {
          setMonitorProducts(resultProducts.data);
        }
        setDocuments([]);
        setLastServicesData([]);
        setLastSetvicesIncludes([]);
        FetchMonitorLastServices(monitor.id);
        setPage(1);
        setLoadingDetails(false);
        setOpenMonitorDetails(true);
      } catch (error) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    if (selectedArea !== null) {
      changeMap(selectedArea);
    }
  }, [
    selectedStationar,
    selectedMonitorTypes,
    selectedArea,
    selectedCheckbox,
    selectedStatuses,
    selectedTrapTypes,
  ]);

  const changeMap = async (i: number, areas: PropertyArea[] | null = null) => {
    const propertyAreasToUse = areas || propertyAreas;
    setLoading(true);
    setMessage("");

    if (!propertyAreasToUse[i]?.map) {
      setLoading(false);
      setMessage("Keine Karte hochgeladen");
    } else {
      setOpenMonitorDetails(true);
      setMap(propertyAreasToUse[i].map);
      setSelectedArea(i);
      try {
        const response = await MonitorService.GetMonitors({
          "filter[propertyAreaId]": propertyAreasToUse[i].id,
          "page[size]": 1000,
          ...(selectedStationar.includes("isWireless") &&
          selectedStationar.includes("isTemporary")
            ? {
                "filter[isDigitalOrTemporary]": true,
              }
            : {
                ...(selectedStationar.includes("isTemporary") && {
                  "filter[isTemporary]": true,
                }),
                ...(selectedStationar.includes("isWireless") && {
                  "filter[isWireless]": true,
                }),
              }),
          ...(selectedMonitorTypes.length > 0 && {
            "filter[monitorTypeId]": selectedMonitorTypes,
          }),
          ...(selectedCheckbox && {
            "filter[infestationOnly]": true,
          }),
          ...(selectedStatuses.length > 0 && {
            "filter[digital_statuses][]": selectedStatuses,
          }),
          ...(selectedTrapTypes.length > 0 && {
            "filter[trap_types]": selectedTrapTypes,
          }),
          include: "monitorType,traplinkedReports,traplinkedReports.comments",
        });

        const freshMonitors = response.data || [];
        if (
          freshMonitors.length === 0 &&
          (selectedStationar.length !== 0 || selectedMonitorTypes.length !== 0)
        )
          setMessage("Keine Ergebnisse für die aktuelle Filterkonfiguration");
        setMonitors(freshMonitors);

        if (pendingDeepLink) {
          const monitorIndex = freshMonitors.findIndex(
            (m: any) => m.id === pendingDeepLink.monitorId
          );

          if (monitorIndex !== -1) {
            handlePinClick(monitorIndex, freshMonitors);
            setPendingDeepLink(null);
          }
        }

        if (freshMonitors.length > 0) {
          const mappedCrops = freshMonitors.map((monitor: any) => ({
            x: Number(monitor.x),
            y: Number(monitor.y),
          }));

          const mappedMonitorPinAbbrs = freshMonitors.map((monitor: any) => ({
            name: monitor.monitorType?.label?.slice(0, 3).toUpperCase() ?? "",
            number:
              (monitor.isTemporary ? "T" : "") + monitor.position.toString(),
          }));

          const mappedMonitorTypes: MonitorTypeItem[] = Array.from(
            new Map<string, MonitorTypeItem>(
              freshMonitors.map((monitor: any) => [
                monitor.monitorType?.id ?? `unknown-${Math.random()}`,
                {
                  id: monitor.monitorType?.id ?? "",
                  label: monitor.monitorType?.label ?? "",
                } as MonitorTypeItem,
              ])
            ).values()
          );

          setCrops(mappedCrops);
          setMonitorPinAbbrs(mappedMonitorPinAbbrs);
          if (selectedMonitorTypes.length === 0)
            setMonitorTypes(mappedMonitorTypes);
          if (!pendingDeepLink) {
            handlePinClick(0, freshMonitors);
          }

          setIsPinSelected(true);
        } else {
          setOpenMonitorDetails(false);
          setCrops([{ x: 1, y: 1 }]);
          setMonitorPinAbbrs([{ name: "", number: "" }]);
        }
      } catch (error) {
        setMessage("Monitore konnten nicht geladen werden");
        setOpenMonitorDetails(false);
        setCrops([{ x: 1, y: 1 }]);
        setMonitorPinAbbrs([{ name: "", number: "" }]);
        setMonitorTypes([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChangeTab = (tab: any, i: any) => {
    flushSync(() => {
      setCurrentTab(tab);
      setMap(null);
      setCrops([]);
      setMonitorPinAbbrs([]);
      setIsPinSelected(true);
      handleResetFilter();
      setSelectedArea(i);
    });
  };

  const handleDownloadMonitorPlan = async () => {
    const selectedPropertyArea = propertyAreas[selectedArea];

    const mapImage = mapRef.current
      ? await toPng(mapRef.current, {
          cacheBust: true,
          pixelRatio: 2,
          backgroundColor: "#ffffff",
          filter: (node) => {
            if (node instanceof HTMLElement) {
              return !node.classList.contains("leaflet-control-zoom");
            }

            return true;
          },
        })
      : null;

    if (!selectedPropertyArea?.id) {
      console.error("No property area selected.");
      return;
    }

    if (!mapImage) {
      throw new Error("Failed to export map image.");
    }

    try {
      const { mapImageWidth, mapImageHeight } = await new Promise<{
        mapImageWidth: number;
        mapImageHeight: number;
      }>((resolve, reject) => {
        const image = new window.Image();

        image.onload = () => {
          resolve({
            mapImageWidth: image.naturalWidth,
            mapImageHeight: image.naturalHeight,
          });
        };

        image.onerror = () => {
          reject(new Error("Failed to read exported map dimensions."));
        };

        image.src = mapImage;
      });

      const response = await PropertyAreaService.GetPropertyAreaMonitorPlan(
        selectedPropertyArea.id
      );

      const planData = response.data;

      const document = (
        <MonitorPlanPDF
          monitorPlan={planData}
          mapImage={mapImage}
          mapImageWidth={mapImageWidth}
          mapImageHeight={mapImageHeight}
        />
      );

      const blob = await pdf(document).toBlob();
      const url = URL.createObjectURL(blob);

      const link = window.document.createElement("a");
      link.href = url;
      link.download = `MonitorPlan-${selectedPropertyArea.name}.pdf`;

      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error("Failed to download monitor plan PDF:", error);
    }
  };

  const FetchMonitorLastServices = async (id: string) => {
    if (id != "") {
      setIsLoadingLastServices(true);
      const isIdChanged = prevMonitorId.current != id;

      if (isIdChanged) {
        setLastServicesData([]);
        setLastSetvicesIncludes([]);
        setDocuments([]);
      }

      prevMonitorId.current = id;
      try {
        const result: any = await MonitorService.GetMonitorLastServices(
          id,
          isIdChanged ? 1 : page,
          10
        );

        if (result && Array.isArray(result.data)) {
          setLastServicesData((prevData: any) => [
            ...(prevData ?? []),
            ...(result.data ?? []),
          ]);

          setLastSetvicesIncludes((prevData: any) => [
            ...(prevData ?? []),
            ...(result.includes?.job ?? []),
          ]);

          setDocuments((prevData: any) => [
            ...(prevData ?? []),
            ...(result.includes?.documents ?? []),
          ]);

          setResultMeta(result.meta);
        }
      } catch (error) {
        console.error("Failed to fetch monitor services:", error);
      } finally {
        setIsLoadingLastServices(false); // stop loading
      }
    }
  };

  const handleFilter = () => {
    if (showFilter) {
      setShowFilter(false);
    } else setShowFilter(true);
  };

  const handleCloseFilter = () => {
    setShowFilter(false);
  };

  const handleApplyFilter = (
    fromDate: string,
    toDate: string,
    propertyAreas: string[],
    appointmentTypes: string[],
    selectedStationar: string[],
    selectedMonitorTypes: string[],
    selectedCheckbox: boolean,
    selectedStatuses: string[],
    selectedTrapTypes: string[]
  ) => {
    setShowFilter(false);
    setSelectedMonitorTypes(selectedMonitorTypes);
    setSelectedStationar(selectedStationar);
    setSelectedCheckbox(selectedCheckbox);
    setSelectedStatuses(selectedStatuses);
    setSelectedTrapTypes(selectedTrapTypes);

    if (
      selectedStationar.length ||
      selectedMonitorTypes.length ||
      selectedCheckbox === true ||
      selectedStatuses.length ||
      selectedTrapTypes.length
    )
      setFiltered(true);
    else setFiltered(false);
  };

  const handleResetFilter = () => {
    setSelectedStationar([]);
    setSelectedMonitorTypes([]);
    setSelectedStatuses([]);
    setSelectedTrapTypes([]);
    setShowFilter(false);
    setSelectedCheckbox(false);
    setFiltered(false);
  };

  return (
    <AppLayout>
      <div className={styles["customer-monitors"]}>
        <div className={styles["customer-monitors__heading"]}>
          <p className={styles["customer-monitors__title"]}>
            <MonitorsIcon /> Monitore
          </p>
        </div>

        <div className={styles["tabs"]}>
          <div className={styles["buttons"]}>
            {subMenuButtons.length > 0 &&
              subMenuButtons.map((button, i) => (
                <SubMenuButton
                  currentTab={currentTab}
                  value={button}
                  onSetTab={() => handleChangeTab(button, i)}
                  key={i}
                />
              ))}
          </div>

          <div className={styles["right-side"]}>
            <div className={styles["filter-wrapper"]}>
              {showFilter ? (
                <Filters
                  showStationarFilter
                  showMonitorTypesFilter
                  showCheckbox
                  stationarOptions={stationarTypes}
                  selectedStationarOptions={selectedStationar}
                  monitorTypesOptions={monitorTypes}
                  monitorTypes={selectedMonitorTypes}
                  statusesOptions={statusesOptions}
                  selectedStatusesOptions={selectedStatuses}
                  trapTypesOptions={trapTypeOptions}
                  selectedTrapTypesOptions={selectedTrapTypes}
                  selected={selectedCheckbox}
                  onClose={handleCloseFilter}
                  showStatusesFilter
                  onApplyFilter={handleApplyFilter}
                  buttonWidth={100}
                />
              ) : (
                filtered && (
                  <p
                    className={styles["filters__reset-text"]}
                    onClick={handleResetFilter}
                  >
                    {t("ResetFilter")}{" "}
                  </p>
                )
              )}
            </div>

            <CustomButton
              icon={filtered ? "filterLight" : "filterDark"}
              color={filtered ? "green" : "lightGray"}
              width={36}
              onClick={handleFilter}
            />
            <CustomButton
            icon="download"
            color="gray"
            width={36}
            onClick={handleDownloadMonitorPlan}
          />
          </div>
          
        </div>
      </div>

      {!loading &&
        map &&
        crops.length > 0 &&
        monitorPinAbbrs.length > 0 &&
        message === "" && (
          <div className={styles["image-container"]}>
            <div className={styles["image"]} ref={mapRef}>
              <PinCropMap
                map={map}
                crop={crops}
                monitorPinAbbr={monitorPinAbbrs}
                onCropChange={() => {}}
                isDraggable={true}
                onPinClick={handlePinClick}
                isPinSelected={isPinSelected}
                selectedPinIndex={selectedPinIndex}
              />
            </div>

            {openMonitorDetails && (
              <MonitorDetails
                monitorDetails={monitorDetails}
                lastServicesData={lastServicesData}
                lastServicesIncludes={lastServicesIncludes}
                propertyAreaId={propertyAreas[selectedArea].id}
                documents={documents}
                monitorProducts={monitorProducts}
                loading={loadingDetails}
                isFetching={isFetching}
                isLoadingLastServices={isLoadingLastServies}
                deepLinkReportId={deepLinkReportId ?? undefined}
                deepLinkAction={deepLinkAction ?? undefined}
                onDeepLinkHandled={() => setPendingDeepLink(null)}
              />
            )}
          </div>
        )}

      {loading && <LoadingSpinner loading={loading} size={30} />}
      {message !== "" && (
        <NoData>
          <p>{message}</p>
        </NoData>
      )}
    </AppLayout>
  );
};
